// packages/front/src/ideas/ideas-page.tsx
//
// Ideas screen — standalone page for the Ideas feature (nurture cycle).
// SmartTable with inline edit, Add Idea modal, delete with confirm.
// P&L per idea is derived from portfolio positions joined by symbolId
// (reuses usePortfolioPositions — no duplication of positions logic).

import type { ColumnDef } from "@tanstack/react-table";
import { Pencil, Trash2 } from "lucide-react";
import { useCallback, useMemo, useState, type FC } from "react";
import { z } from "zod";
import {
  Button,
  DataPage,
  EmptyState,
  ErrorState,
  formatDisplayNumber,
  Loading,
  SmartTable,
  ToastContainer,
  useToast,
  type FilterClause,
  type SmartTableColumnMeta,
  type SortClause,
} from "../_common";
import { usePortfolioPositions } from "../portfolio";
import {
  useDeleteIdea,
  useIdeas,
  useUpdateIdea,
  type Idea,
} from "./ideas.hooks";
import { AddIdeaModal } from "./modals/add-idea-modal";

const ideaInlineUpdateSchema = z
  .object({
    status: z.string().optional(),
    strategy: z.string().nullable().optional(),
    description: z.string().optional(),
    pros: z.string().nullable().optional(),
    cons: z.string().nullable().optional(),
    note: z.string().nullable().optional(),
    date: z.string().optional(),
  })
  .strict();

function formatUSD(v: number | undefined | null): string {
  if (v === undefined || v === null || Number.isNaN(v)) return "—";
  const value = Math.abs(v);
  const formatted = formatDisplayNumber(value, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return v < 0 ? `-$${formatted}` : `$${formatted}`;
}

export const IdeasPage: FC = () => {
  const { toasts, dismissToast, showToast } = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [editIdea, setEditIdea] = useState<Idea | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [filters, setFilters] = useState<FilterClause[]>([]);
  const [sort, setSort] = useState<SortClause[]>([]);

  // ── Server-state reads (TanStack Query) ───────────────────────────────────
  const ideasQuery = useIdeas();
  // Reuse portfolio positions to derive P&L per idea by symbolId.
  const positionsQuery = usePortfolioPositions();

  const ideas = ideasQuery.data?.items ?? [];
  const positions = positionsQuery.data?.items ?? [];
  const isLoading = ideasQuery.isLoading;
  const error = ideasQuery.error?.message ?? null;

  // Build symbolId → total P&L (realized + unrealized) map from positions.
  const pnlBySymbol = useMemo(() => {
    const map = new Map<string, number>();
    for (const p of positions) {
      const sid = p.symbolId;
      if (!sid) continue;
      const realized = p.realizedPnL ?? 0;
      const unrealized = p.unrealizedPnL ?? 0;
      map.set(sid, (map.get(sid) ?? 0) + realized + unrealized);
    }
    return map;
  }, [positions]);

  // ── Mutations ──────────────────────────────────────────────────────────────
  const updateIdea = useUpdateIdea();
  const deleteIdea = useDeleteIdea();

  const handleInlineEdit = useCallback(
    async (rowId: string, field: string, value: unknown) => {
      try {
        const parsedBody = ideaInlineUpdateSchema.safeParse({
          [field]: value,
        });
        if (!parsedBody.success) {
          throw new Error(`Unsupported idea field: ${field}`);
        }
        await updateIdea.mutateAsync({
          id: rowId,
          body: parsedBody.data,
        });
        showToast("success", `Updated ${field}`);
      } catch (err) {
        const message = err instanceof Error ? err.message : "error";
        showToast("error", `Save failed: ${message}`);
      }
    },
    [updateIdea, showToast],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      if (!confirm("Delete this idea?")) return;
      try {
        await deleteIdea.mutateAsync(id);
        showToast("success", "Idea deleted");
      } catch (err) {
        const message = err instanceof Error ? err.message : "error";
        showToast("error", `Delete failed: ${message}`);
      }
    },
    [deleteIdea, showToast],
  );

  // ── Column definitions ─────────────────────────────────────────────────────
  const columns = useMemo<ColumnDef<Idea>[]>(() => {
    const base: ColumnDef<Idea>[] = [
      {
        accessorKey: "date",
        header: "Date",
        meta: { format: "date" } satisfies SmartTableColumnMeta,
      },
      {
        id: "symbol",
        header: "Symbol",
        cell: ({ row }) => {
          const ticker = row.original.symbolTicker ?? "—";
          const name = row.original.symbolName ?? "";
          return name ? `${ticker} — ${name}` : ticker;
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        meta: { editable: true } satisfies SmartTableColumnMeta,
      },
      {
        accessorKey: "strategy",
        header: "Strategy",
        meta: { editable: true } satisfies SmartTableColumnMeta,
      },
      {
        accessorKey: "description",
        header: "Description",
        meta: { editable: true } satisfies SmartTableColumnMeta,
      },
      {
        accessorKey: "pros",
        header: "Pros",
        meta: { editable: true } satisfies SmartTableColumnMeta,
      },
      {
        accessorKey: "cons",
        header: "Cons",
        meta: { editable: true } satisfies SmartTableColumnMeta,
      },
      {
        accessorKey: "note",
        header: "Note",
        meta: { editable: true } satisfies SmartTableColumnMeta,
      },
      {
        id: "pnl",
        header: "P&L",
        meta: { align: "right" } satisfies SmartTableColumnMeta,
        cell: ({ row }) => {
          const sid = row.original.symbolId;
          if (!sid) return "—";
          return formatUSD(pnlBySymbol.get(sid) ?? null);
        },
      },
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        enableColumnFilter: false,
        meta: {
          filterable: "none",
          sortable: "none",
          isAction: true,
        } satisfies SmartTableColumnMeta,
        cell: ({ row }) => {
          const id = row.original.id;
          if (!id) return null;
          return (
            <div className="flex gap-0.5" onClick={(e) => e.stopPropagation()}>
              <Button
                variant="outline"
                size="icon"
                className="h-6 w-6 border-blue-400 text-blue-600 hover:bg-blue-50 p-0"
                aria-label="Edit"
                title="Edit"
                onClick={() => setEditIdea(row.original)}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-6 w-6 border-red-500 text-red-600 hover:bg-red-50 p-0"
                aria-label="Delete"
                title="Delete"
                onClick={() => handleDelete(id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          );
        },
      },
    ];
    return base;
  }, [handleDelete, pnlBySymbol]);

  const actions = (
    <Button size="sm" onClick={() => setModalOpen(true)}>
      + Add Idea
    </Button>
  );

  return (
    <DataPage title="Ideas" actions={actions}>
      <div className="space-y-4">
        {isLoading ? (
          <Loading />
        ) : error ? (
          <ErrorState message={error} />
        ) : ideas.length === 0 ? (
          <EmptyState message="Нет идей. Добавьте первую идею.">
            <Button
              size="sm"
              className="mt-2"
              onClick={() => setModalOpen(true)}
            >
              + Add Idea
            </Button>
          </EmptyState>
        ) : (
          <SmartTable<Idea>
            columns={columns}
            data={ideas}
            total={ideas.length}
            page={page}
            pageSize={pageSize}
            isLoading={false}
            error={null}
            filters={filters}
            sort={sort}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            onFiltersChange={(f) => {
              setFilters(f);
              setPage(1);
            }}
            onSortChange={setSort}
            onInlineEdit={handleInlineEdit}
            rowIdField="id"
          />
        )}
      </div>

      {/* Add modal (create mode) */}
      <AddIdeaModal open={modalOpen} onClose={() => setModalOpen(false)} />

      {/* Edit modal (edit mode via editId + initialData) */}
      <AddIdeaModal
        open={editIdea !== null}
        editId={editIdea?.id}
        initialData={editIdea ?? undefined}
        onClose={() => setEditIdea(null)}
      />

      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </DataPage>
  );
};
